import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { User } from '@core/modules/user/models';
import { DeviceUserFormControlName } from '../../models';
import { MyDevicesFormState } from '../../types/my-devices-form-builder-service.type';
import { MyDeviceFormService } from '../../services/my-device-form.service';
import { DeviceUser } from '@core/models/device-user';
import { DeviceBlockService } from '../../services/device-block.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

@Component({
    selector: 'app-registered-device',
    templateUrl: './registered-device.component.html',
    styleUrls: ['./registered-device.component.scss'],
    standalone: false
})
export class RegisteredDeviceComponent implements OnInit {
  @Input() idx!: number;
  @Input() user!: User;
  @Input() form!: FormGroup;
  @Input() deviceControl!: AbstractControl;
  @Input() myDevicesFormState!: MyDevicesFormState;
  @Input() hasProfile!: boolean;

  @Output() unlinkDevice: EventEmitter<{ userId: number; device: DeviceUser }> = new EventEmitter<{
    userId: number;
    device: DeviceUser;
  }>();
  @Output() changedPassphraseValue: EventEmitter<string> = new EventEmitter<string>();

  errorFeedbackList = this.deviceBlockService.loadFeedbackErrorLists();

  // serial form control
  get serialControl(): FormControl {
    return this.deviceControl.get(DeviceUserFormControlName.SERIAL) as FormControl;
  }

  get isValidSerial() {
    return this.serialControl.valid;
  }

  get isSubmittedSerialControl() {
    return this.deviceBlockService.isSubmittedSerialControl(this.serialControl);
  }
  // ===

  // passphrase form control
  get passphraseControl(): FormControl {
    return this.deviceControl.get(DeviceUserFormControlName.PASSPHRASE) as FormControl;
  }

  get isSubmittedPassphraseControl() {
    return this.deviceBlockService.isSubmittedPassphraseControl(this.passphraseControl);
  }
  // ===

  get userDevice() {
    const serial = this.serialControl.value;

    if (!serial) return undefined;

    return this.user.devices.find((device) => device.serial.toUpperCase() === serial.toUpperCase());
  }

  get hasDevicePassphrase(): boolean {
    return !!this.userDevice?.passphrase;
  }

  get isDisabled(): boolean {
    return this.hasDevicePassphrase;
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly deviceBlockService: DeviceBlockService,
    private readonly myDeviceFormService: MyDeviceFormService
  ) {}

  ngOnInit(): void {
    this.errorFeedbackList = this.deviceBlockService.loadFeedbackErrorLists();

    this.form.statusChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.deviceBlockService.resetSubmittedWhenPassphraseError(
            this.myDevicesFormState,
            this.passphraseControl
          );
        })
      )
      .subscribe();
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (this.isDisabled) {
  //     this.deviceControl?.disable();
  //     this.passphraseControl?.disable();
  //   } else {
  //     this.passphraseControl?.enable();
  //   }
  // }

  onChangedSerial(value: string): void {
    this.deviceBlockService.onChangedSerial(
      this.form,
      this.myDevicesFormState,
      this.serialControl,
      value
    );
  }

  onUseSharedDevice(useSharedDevice: boolean): void {
    this.deviceBlockService.onUseSharedDevice(
      this.form,
      this.myDevicesFormState,
      <FormGroup>this.deviceControl,
      useSharedDevice
    );
  }

  onChangedPassphrase(value: string): void {
    this.changedPassphraseValue.emit(value);
  }

  onOpenSharedDeviceInfo(): void {
    this.myDeviceFormService.openSharedDeviceInfo();
  }

  onOpenSerialInfo(): void {
    this.myDeviceFormService.openSerialInfo();
  }

  async onUnlinkSerial(user: User) {
    const serial = this.serialControl.value;
    const unlinkDevice = user.devices.find((device) => {
      return device.serial.toUpperCase() === serial.toUpperCase();
    });
    if (unlinkDevice) {
      this.unlinkDevice.emit({ userId: user.id, device: unlinkDevice });
    }
  }

  isShowProblemFormFor(): boolean {
    return this.deviceBlockService.isShowProblemFormFor(this.serialControl);
  }
}
